import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import TooltipMUI from '@material-ui/core/Tooltip'
import Zoom from '@material-ui/core/Zoom'

import CustomModal, { ModalMain } from '../modal/CustomModal'
import { icons } from '../settings/RatingCriteriaIconOptions'

const CustomModalMain = styled(ModalMain)`
  width: 1000px;
  padding: 4rem 1rem;
  text-align: center;

  h3 {
    margin-bottom: 3rem;
  }
`

const strokeColors = [
  '#8884d8',
  '#61be65',
  '#f3e200',
  '#ffa632',
  '#ff4c4c',
  '#D34E24',
  '#3f51b5',
  '#61a8be',
  '#bc61be',
  '#dedede',
  '#a74c32',
  '#b6a55c',
  '#ffa8ad',
  '#710627',
  '#38726C',
  '#BAA898',
  '#A57548',
  '#34435E',
  '#AA6373',
  '#03440C',
  '#FF8552',
  '#39393A',
  '#DF3B57',
  '#717744'
]

const renderLines = (arr, opacity) =>
  arr.map((reviewer, i) => (
    <Line
      key={reviewer}
      type="monotone"
      dataKey={reviewer}
      stroke={strokeColors[i]}
      strokeWidth="2"
      strokeOpacity={opacity[reviewer]}
    />
  ))

const renderCustomAxisTick = specialistsRatingCriteria =>
  function AxisTick({ x, y, payload }) {
    const criteria = specialistsRatingCriteria.find(
      criterion => criterion.label === payload.value
    )

    if (!criteria || !icons[criteria.iconName]) return null

    const IconComponent = icons[criteria.iconName]

    const iconSize = 24
    const iconOffset = iconSize / 2

    return (
      <TooltipMUI
        TransitionComponent={Zoom}
        title={criteria.label}
        placement="bottom"
      >
        <g transform={`translate(${x - iconOffset},${y})`}>
          <rect
            x={0}
            y={-iconOffset}
            width={iconSize}
            height={iconSize}
            fill="transparent"
          />
          <IconComponent size={iconSize} />
        </g>
      </TooltipMUI>
    )
  }

const createLegendOpacityObj = lineKeyDataArr => {
  const outputObj = { opacity: {} }

  lineKeyDataArr.forEach(reviewer => {
    outputObj.opacity[reviewer] = 1
  })

  return outputObj
}

const MultipleLinesChartForSpecialists = ({
  isOpen,
  close,
  allRates,
  proposal,
  specialistsRatingCriteria = []
}) => {
  const { t } = useTranslation('proposals')

  const lineKeyDataArr =
    allRates && allRates.rates && allRates.rates.length > 0
      ? Object.keys(allRates.rates[0]).filter(e => e !== 'name')
      : []
  const legendOpacityObj = createLegendOpacityObj(lineKeyDataArr)
  const [linesOpacity, setLinesOpacity] = useState(legendOpacityObj)

  const handleMouseEnter = o => {
    const { dataKey } = o

    setLinesOpacity({
      opacity: {
        ...linesOpacity.opacity,
        [dataKey]: 0.5
      }
    })
  }

  const handleMouseLeave = o => {
    const { dataKey } = o

    setLinesOpacity({
      opacity: {
        ...linesOpacity.opacity,
        [dataKey]: 1
      }
    })
  }

  const chartData =
    allRates?.rates?.length > 0
      ? allRates.rates
      : (specialistsRatingCriteria || []).map(c => ({ name: c.label }))

  return (
    <CustomModal show={isOpen} handleClose={close}>
      <CustomModalMain>
        <h3 style={{ marginTop: '40px' }}>
          {t('Proposal')} {proposal.number} - {proposal.name}
        </h3>

        <LineChart
          width={800}
          height={550}
          data={chartData}
          style={{
            cursor: 'pointer',
            display: 'flex',
            margin: '0 auto'
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            interval={0}
            angle={30}
            height={50}
            tick={renderCustomAxisTick(specialistsRatingCriteria)}
          />
          <YAxis type="number" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} />
          <Tooltip />
          <Legend
            layout="vertical"
            verticalAlign="top"
            align="right"
            wrapperStyle={{ right: '0', transform: 'translateX(30%)' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />

          {renderLines(lineKeyDataArr, linesOpacity.opacity)}
        </LineChart>
      </CustomModalMain>
    </CustomModal>
  )
}

MultipleLinesChartForSpecialists.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  close: PropTypes.func.isRequired,
  allRates: PropTypes.object,
  proposal: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.number
  ]).isRequired,
  specialistsRatingCriteria: PropTypes.array
}

export default MultipleLinesChartForSpecialists
